import { useLayoutEffect, useRef } from "react"
import { animate, clamp, transform, motion, useMotionValue } from "framer-motion"
import frameSync, { cancelSync as cancelFrameSync } from "framesync"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useDir } from "contexts/Locale"
import { useSuspender } from "contexts/Suspender"

import getMediaQuery, { breakpoint as breakpoints } from "css/breakpoints"
import { a11yOutline } from "css/a11y"

import { WIDTHS } from "components/media/ImageCLD"
import { useVariations } from "components/family-variations/Variations"
import Tracker from "components/family-variations/Tracker"
import Icon from "components/family-variations/Icon"
import Switch, { Switcher } from "components/family-variations/Switch"

const Root = styled(motion.div)`
  z-index: 3;
  position: relative;
  pointer-events: none;

  grid-column: doc;
  grid-row: ballerina;
  min-width: 90%;
  width: 90%;
  max-width: 90%;
  margin: 0 auto;
  /* height: min(512px, calc(var(--grid-col-unit) * 6 + var(--grid-gap) * 5)); */

  ${getMediaQuery("m")} {
    grid-column: col 4 / span 6;
    min-width: 80%;
    width: 80%;
    max-width: 80%;
    margin: 0 auto;
  }

  & > canvas {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  & .track {
    position: absolute;
    inset: 0;
    &.focus-visible {
      ${a11yOutline}
    }
  }

  & > svg {
    fill: rgb(var(--theme));
  }
`

class Ballerina extends Array {
  static async #load(path, i, { console, origin }) {
    const img = new Image()

    const loadPromise = new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
    })

    img.decoding = "async"
    //https://content.rlx-uat.com/rolex-v7/dam/test/3d-configurator/960x960_webp_icc_q100/m126300-0008/m126300-0008_233.webp
    const frame = i.toString().padStart(3, "0")
    img.srcset = `${origin}${path.replace("%i%", frame)}`

    img.sizes = `(max-width: ${breakpoints.l}) 100vw, 50vw`

    console.silly("Ballerina#load(%s, %s)", path, i, img)
    try {
      await loadPromise
      console.verbose("Ballerina(%s)#load(%i):success => %s", path, i, img.currentSrc)
      return img
    } catch (err) {
      console.verbose("Ballerina(%rmc)#load(%s):failed", path, i)
      console.error(err)
      return null
    }
  }

  constructor({ rmc, titleCode, family, title, frames = 0, start = 0, bracelet_id, case_id, env, console }) {
    super()
    this.#rmc = rmc
    this.#family = family
    this.#title = title
    this.#titleCode = titleCode
    this.#console = console
    this.length = this.#frames = frames
    this.#start = start
    this.#origin = `https://${env.cloudinary.secureDistribution}`
    this.#catalogYear = env.catalogYear

    this.#path = `/image/upload/c_limit,w_960/q_auto:low,f_auto/v1/catalogue/${env.catalogYear}/360/${case_id}-${bracelet_id}/${case_id}-${bracelet_id}--%i%.webp`
    // this.loadSingleFrame(0).then(() => console.verbose("Ballerina(%s):poster"))
  }

  get ballerina() {
    return true
  }

  #path
  get path() {
    return this.#path
  }

  #console
  get console() {
    return this.#console
  }

  #origin
  get origin() {
    return this.#origin
  }

  #catalogYear
  get catalogYear() {
    return this.#catalogYear
  }

  #frames
  get frames() {
    return this.#frames
  }

  #pointer
  #pointertimer
  get pointer() {
    return this.#pointer ?? 0
  }
  set pointer(v) {
    this.#pointer = +v

    clearTimeout(this.#pointertimer)
    this.#pointertimer = setTimeout(() => {
      if (!this[this.#pointer]) this.loadSingleFrame(this.#pointer)
    }, 300)
  }

  #family
  get family() {
    return this.#family
  }

  #titleCode
  get titleCode() {
    return this.#titleCode
  }

  #title
  get title() {
    return this.#title
  }

  #rmc
  get rmc() {
    return this.#rmc
  }

  #scale
  get scale() {
    return this.#scale ?? 1
  }
  set scale(v) {
    this.#scale = +v
  }

  #start
  get start() {
    return this.#start
  }

  get percent() {
    return clamp(0, 1, this.#progress / this.length)
  }

  #progress
  get progress() {
    return this.#progress ?? 0
  }
  set progress(v) {
    this.#progress = +v
    if (typeof this.#onprogress === "function") this.#onprogress(this.percent)
  }
  #onprogress
  get onprogress() {
    return this.#onprogress
  }
  set onprogress(v) {
    this.#onprogress = v
  }

  async poster() {
    return await this[0]
  }

  async ready() {
    return this.#loaded
  }

  #loadPromise
  #loaded
  loadAllFrames() {
    if (!this.#loadPromise) {
      for (let i = 1; i < this.frames; i++) {
        if (!this[i]) {
          this[i] = Ballerina.#load(this.path, this.i(i), { console: this.console, origin: this.origin })
          this[i].then(() => (this.progress += 1))
        }
      }
      this.#loadPromise = Promise.all(this)
      this.#loadPromise.then(() => (this.#loaded = true))
    }

    return this.#loadPromise
  }

  loadSingleFrame(i) {
    if (!this[i]) {
      this[i] = Ballerina.#load(this.path, this.i(i), { console: this.console, origin: this.origin })
      this[i].then(() => (this.progress += 1))
    }
    return this[i]
  }

  i(i) {
    if (i <= this.start) return this.frames - this.start + i - 1
    return i - this.start - 1
  }
}

class PinchZoom extends Ballerina {
  static async #load(rmc, i, { console, origin, catalogYear }) {
    const img = new Image()

    const loadPromise = new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
    })

    img.decoding = "async"
    img.srcset = WIDTHS.reduce((acc, width) => {
      acc.push(`${origin}/image/upload/q_auto,f_auto/t_v7-majesty/c_limit,w_${width}/v1/catalogue/${catalogYear}/upright-c/${rmc} ${width}w`)
      return acc
    }, []).join(", ")

    img.sizes = `(min-width: ${breakpoints.m}) 70vw, 40vw`

    console.silly("Ballerina#load(%s, %s)", rmc, i, img)
    try {
      await loadPromise
      console.verbose("Ballerina(%s)#load(%i):success => %s", rmc, i, img.currentSrc)
      return img
    } catch (err) {
      console.verbose("Ballerina(%rmc)#load(%s):failed", rmc, i)
      console.error(err)
      return null
    }
  }

  constructor({ rmc, family, title, titleCode, frames = 0, start = 0, bracelet_id, case_id, env, console }) {
    super({ rmc, family, title, titleCode, frames, start, bracelet_id, case_id, env, console })
    this.#origin = `https://${env.cloudinary.secureDistribution}`
  }

  get ballerina() {
    return false
  }

  async ready() {
    return this.#loaded
  }

  #pointer
  set pointer(v) {
    this.#pointer = v
  }
  get pointer() {
    return 0
  }

  #origin
  get origin() {
    return this.#origin
  }

  #loadPromise
  #loaded
  loadAllFrames() {
    return this.loadSingleFrame()
  }

  loadSingleFrame() {
    if (!this[0]) {
      this[0] = PinchZoom.#load(this.rmc, 1, { console: this.console, origin: this.origin, catalogYear: this.catalogYear })
      this[0].then(() => (this.progress = this.frames))
    }
    return this[0]
  }
}

export default function Ballerinas({ theme }) {
  const console = useConsole()

  const env = useEnv()
  const htmlDir = useDir()
  const { variation: current, variations, setVariation, start, frames, isFocused } = useVariations()

  const rroot = useRef()
  const htrack = useRef()
  const hasSlid = useRef(false)

  const baseWidth = useMotionValue(0)
  const baseHeight = useMotionValue(0)
  const ratio = useMotionValue(1)
  const scale = useMotionValue(0)
  const width = useMotionValue(0)
  const height = useMotionValue(0)

  const disabled = variations.length <= 1

  const [[err, { canvas, ctx, ...sets }]] = useSuspender(["ballerina", ...variations.map(({ rmc }) => rmc).sort()], async () => {
    try {
      global.ballerinaCache ??= {
        canvas: document.createElement("canvas"),
        store: {},
      }

      const sets = []

      for (const { rmc, familyCode: family, titleCode, title, has360, bracelet_id, case_id } of variations) {
        const Class = has360 ? Ballerina : PinchZoom
        global.ballerinaCache.store[rmc] ??= new Class({ rmc, family, titleCode, title, frames, start, console, env, bracelet_id, case_id })
        global.ballerinaCache.store[rmc].pointer = 0
        global.ballerinaCache.store[rmc].scale = 1
        sets.push(global.ballerinaCache.store[rmc])
      }

      const canvas = global.ballerinaCache.canvas
      const ctx = canvas.getContext("2d")
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      await Promise.all(sets.map(set => set.poster()))

      return [null, { canvas, ctx, ...sets.reduce((acc, set) => ((acc[set.rmc] = set), acc), {}) }]
    } catch (err) {
      console.error(err)
      return [err, null]
    }
  })
  const active = sets[current]
  const inactives = Object.entries(sets).reduce((acc, [key, value]) => (key !== current && acc.push(value), acc), [])

  useLayoutEffect(function addCanvasToDom() {
    const root = rroot.current
    root.appendChild(canvas)
    return () => root.removeChild(canvas)
  })

  useLayoutEffect(function loadPostersWhenIntersects() {
    const tracker = htrack.current

    const observer = new IntersectionObserver(
      ([intersection]) => {
        if (intersection.isIntersecting) {
          Object.values(sets).forEach(set => set.loadSingleFrame(0).then(() => console.verbose("Ballerina(%s):poster", set.rmc)))
          active.poster().then(draw)
        }
      },
      {
        rootMargin: global.screen.availHeight + "px",
      }
    )

    observer.observe(tracker.node)
    return () => {
      observer.unobserve(tracker.node)
      observer.disconnect()
    }
  })

  async function draw(what) {
    const frame = await active[active.pointer]
    if (!frame) return // frames are not guaranteed to be decoded

    ratio.set(frame.width / frame.height)

    canvas.width = /*document.documentElement.clientWidth*/ frame.width * global.devicePixelRatio
    canvas.height = /*document.documentElement.clientHeight*/ frame.height * global.devicePixelRatio

    baseHeight.set(baseWidth.get() * ratio.get())
    const negativeSpace = document.documentElement.clientHeight - baseHeight.get()
    const scaledAddedSpace = (negativeSpace + 200) * scale.get()
    width.set(baseWidth.get() + scaledAddedSpace)
    height.set(width.get() * ratio.get())
    // const x = (canvas.width - width.get() * global.devicePixelRatio) * 0.5
    // const y = (canvas.height - height.get() * global.devicePixelRatio) * 0.5

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(frame, 0, 0, frame.width, frame.height, 0, 0, canvas.width, canvas.height)
    // ctx.strokeStyle = "green"
    // ctx.strokeRect(x, y, width.get() * global.devicePixelRatio, height.get() * global.devicePixelRatio)
  }

  useLayoutEffect(function trackAndDraw() {
    const tracker = htrack.current
    let marked4render = true

    if (!active.ballerina) {
      tracker.force({ x: 0, y: 0, z: 0 })
    }

    let late
    let observer
    new Promise(resolve => {
      observer = new IntersectionObserver(
        ([intersection]) => {
          if (!intersection.isIntersecting) {
            tracker.force({ x: 0, y: 0, z: 0 })
          } else if (intersection.intersectionRatio >= 0.8) {
            resolve()
          }
        },
        {
          threshold: [0.0, 0.8],
        }
      )
    })
      .then(() => active.loadAllFrames())
      .then(async () => {
        if (hasSlid.current || late || (process.browser && document.documentElement.classList.contains("prefers-reduced-motion"))) return

        await new Promise(resolve => setTimeout(resolve, 100))
        return new Promise(resolve => {
          const target = start > frames / 2 ? Math.min(-20, start - frames) : Math.max(20, start)
          animate(0, target, {
            duration: 0.4,
            repeat: 1,
            repeatType: "reverse",
            ease: "easeOut",
            onUpdate: t => onscroll({ scroll: { x: t, z: 0 } }),
            onComplete: () => {
              hasSlid.current = true
              resolve()
            },
          })
        })
      })
      .then(() => active.ballerina && tracker.track())

    const onkeydown = e => {
      const arrow = (e.key.match(/^Arrow((?:Left)|(?:Right))$/) ?? [])[1]
      if (arrow) {
        e.preventDefault()
        const dir = (arrow === "Left" ? -1 : 1) * (htmlDir === "ltr" ? -1 : 1)
        tracker.force({ x: tracker.scroll.x + dir })
        marked4render = true
      }
    }

    const onscroll = ({ scroll: { x, z } } = tracker) => {
      const abs = Math.abs(Math.floor(x)) % active.length

      const lastPointer = active.pointer
      const lastScale = scale.get()
      const nextPointer = !active.ballerina ? start : x < 0 ? active.length - 1 - abs : abs
      const nextScale = transform([0, 1], [0, 1])(z)

      for (const set of [active, ...inactives]) {
        set.pointer = nextPointer
        set.scale = nextScale
        scale.set(nextScale)
      }

      tracker.resize()
      if (lastPointer !== nextPointer || lastScale !== nextScale) marked4render = true
    }

    const onresize = () => {
      const multiplier = transform([320, 1024], [1.2, 0.7])(document.documentElement.clientWidth)
      baseWidth.set(Math.min(document.documentElement.clientWidth, document.documentElement.clientHeight) * multiplier)
      onscroll()
    }

    onresize()

    const loop = frameSync.postRender(async () => {
      if (!marked4render) return
      marked4render = false
      draw()
    }, true)

    global.addEventListener("resize", onresize)
    global.addEventListener("keydown", onkeydown)
    tracker.addEventListener("scroll", onscroll)
    observer?.observe?.(tracker.node)
    return () => {
      late = true
      cancelFrameSync.postRender(loop)
      tracker.silent()
      tracker.removeEventListener("scroll", onscroll)
      global.removeEventListener("resize", onresize)
      global.removeEventListener("keydown", onkeydown)
      observer?.unobserve?.(tracker.node)
      observer?.disconnect?.()
    }
  })

  console.verbose("FamilyVariations:Ballerina(%o)", { variations, variation: current, active, inactives })
  return (
    <Root ref={rroot} /*style={{ "--w": width, "--h": height, "--bw": baseWidth, "--bh": baseHeight }}*/>
      <Tracker
        capturePointer={true}
        ref={htrack}
        isFocused={isFocused}
        wheelDirs={["x", null, null]}
        dragDirs={["x", null, null]}
        x={[0, frames, 0.618]}
        clamp={false}
      />
      <Icon variation={active} theme={theme} />
      {!disabled && (
        <Switch>
          {variations
            .map(variation => sets[variation.rmc])
            .map(
              variation =>
                variation && (
                  <Switcher
                    theme={theme}
                    disabled={disabled}
                    key={variation.rmc}
                    active={active === variation}
                    variation={variation}
                    setVariation={setVariation}
                  >
                    {variation.rmc}
                  </Switcher>
                )
            )}
        </Switch>
      )}
    </Root>
  )
}
